<script setup lang="ts">
import { computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlanetStore } from '@/stores/planetStore.ts'
import MediumModel from '@/components/Interface/Modal/ModelSizes/MediumModel.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import Backend from '@/models/backend'
import NamedNumbers from '@/components/Elements/Generic/NamedNumbers.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'

// Store
const gameStore = useGameStore()
const planetStore = usePlanetStore()

// Backend instance
const backend = new Backend()

// Computed property for two-way binding with the slider
const alliancePercentage = computed({
  get: () => gameStore.player?.allianceSciencePointsAllocationPercentage ?? 0,
  set: (value: number) => {
    if (gameStore.player) {
      gameStore.player.allianceSciencePointsAllocationPercentage = value
      // Update backend with new percentage
      backend.updateAllianceSciencePercentage(value)
    }
  }
})

// Calculate personal science (what the player keeps)
const personalScienceOutput = computed(() => {
  const total = planetStore.getRawTotalScienceOutput()
  const percentage = alliancePercentage.value
  return Math.ceil((total * (100 - percentage)) / 100)
})

// Calculate alliance science (what goes to the alliance)
const allianceScienceOutput = computed(() => {
  const total = planetStore.getRawTotalScienceOutput()
  const percentage = alliancePercentage.value
  return Math.ceil((total * percentage) / 100)
})
</script>

<template>
  <MediumModel title="Alliance Science Allocation">
    <div class="p-2">
      <StandardCard top-padding="0">
        Allocate a percentage of your science points to your alliance. The contributed science will be used to develop a prototype of a technology. Once the prototype is complete, you’ll be able to research the technology at a <span class="text-green-400">{{ gameStore.player?.allianceResearchDiscountPercentage || 0 }}%</span> discount.
      </StandardCard>
      <StandardCard top-padding="1">
        <div class="flex items-center gap-4 mb-4">
          <span class="text-sm text-slate-300 whitespace-nowrap font-medium"
            >Alliance Allocation: {{ alliancePercentage }}%</span
          >
          <div class="relative flex-1">
            <input
              v-model="alliancePercentage"
              type="range"
              min="0"
              max="100"
              step="5"
              class="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
            />
          </div>
        </div>

        <!-- Science allocation display -->
        <div class="flex justify-between items-center mt-2">
          <div>
            <NamedNumbers :text="`Personal (${100 - alliancePercentage}%)`">
              <GenericIconWithNumber type="science" :amount="personalScienceOutput" />
            </NamedNumbers>
          </div>
          <div>
            <NamedNumbers :text="`Alliance (${alliancePercentage}%)`">
              <GenericIconWithNumber type="blueprint" :amount="allianceScienceOutput" />
            </NamedNumbers>
          </div>
        </div>
      </StandardCard>
    </div>
  </MediumModel>
</template>

<style scoped lang="scss"></style>
